import { getClientId, getRawAppName, getRequestState, isRequestFromOidcClient } from './widget-facade';
import { PASSWORD_RESET_URL_TO_CLIENT_MAPPING } from './app-utils';
import { APP_TYPES, AppType, CLIENT_ID_TO_SUPPORT_ENV_MAPPING, LanguageType } from '../constants';
import logger from '../../logger';
import AmplitudeUtils from './amplitude-utils';
import { fetchPreAuthnConfigs, PreAuthnConfigsResponse } from './iam-core-client';
import athenaConfig from '../config';
import { nanoid } from 'nanoid';
import { setAndGetLwaUniqId } from './storage-utils';

const { authServerId, lwaSpaClientIds } = athenaConfig;
const DIRECT_OHSW_PRE_AUTHN_APP_TYPES = [
  APP_TYPES.ADJACENT_AONE_ATHENISTA_AUTH,
  APP_TYPES.ADJACENT_ATHENISTA,
];

interface AppConfig {
  appType?: AppType;
  i18n?: Record<LanguageType, any>;
  supportEnv?: string;
  disableIdpDiscovery?: boolean;
  allowRegistration?: boolean;
}

interface PreAuthnRequestPayload {
  oauthCodeChallenge?: string;
  emailToken?: string;
  fhir?: boolean;
  idp?: string;
  xRoutingDeployment?: string;
}

interface PreAuthnConfigs extends PreAuthnConfigsResponse {
  appLabel?: string;
}

export interface AppSettings extends PreAuthnConfigs, AppConfig {
  isAuthnWithLwaClientId?: boolean;
  clientId: string;
  postToOkta: boolean;
  unset: (key: keyof PreAuthnConfigsResponse) => void;
  lwaUniqId?: string;
  isDegradedState?: boolean;
  xRoutingDeployment?: string;
}

function redirectPasswordResetByDbInstance(): void {
  const pathname = window.location.pathname;
  const currentHost = window.location.host;
  const currentHref = window.location.href;

  // Safety check: ensure we have a valid href
  if (!currentHref) {
    return;
  }

  const currentUrl = new URL(currentHref);

  // Don't redirect if client_id is already in the URL
  if (currentUrl.searchParams.has('client_id')) {
    return;
  }

  for (const [ urlPattern, hostMappings ] of Object.entries(PASSWORD_RESET_URL_TO_CLIENT_MAPPING)) {
    if (pathname === urlPattern) {
      // Find the matching host configuration
      const hostConfig = hostMappings.find(mapping => mapping.host === currentHost);

      if (hostConfig) {
        // Redirect to the standard forgot-password URL with client_id for the DB instance
        const targetUrl = new URL(window.location.origin + '/signin/forgot-password');
        targetUrl.searchParams.set('client_id', hostConfig.clientId);
        window.location.href = targetUrl.toString();
      }
      return;
    }
  }
}

function getParsedState(): Record<string, any> {
  const state = getRequestState();
  try {
    return (state && JSON.parse(state)) || {};
  }
  catch (err) {
    // eslint-disable-next-line no-empty
  }
  return {};
}

function getRequestingClientId(requestContext: any, originalRedirectUri?: URL) {
  if (originalRedirectUri) {
    return originalRedirectUri?.searchParams?.get('client_id') || getParsedState().client_id;
  }

  // For /signin/forgot-password flow, use athenaOne client_id from URL parameter to determine user's namespace
  if (window.location.pathname === '/signin/forgot-password') {
    const clientIdFromUrl = new URL(window.location.href).searchParams.get('client_id');
    if (clientIdFromUrl) {
      return clientIdFromUrl;
    }
  }

  return getClientId(requestContext);
}

export function getRequestingOriginalRedirectUri(): undefined | URL {
  if (isAuthnWithLwaSpaClient() || isAuthnWithDirectOhswPreAuthnClient()) {
    try {
      const { originalRedirectUri } = getParsedState();
      if (originalRedirectUri) {
        return new URL(originalRedirectUri);
      }
    }
    catch (err) {
      logger.error('Failed to parse originalRedirectUri', err);
    }
  }
}

function getAppConfig(preAuthnConfigs: PreAuthnConfigs, clientId?: string) {
  const { appLabel } = preAuthnConfigs;
  const appNameForConfig = getAppNameForConfig(appLabel);
  let appConfig: AppConfig = {};

  if (appConfigs) {
    if (appNameForConfig && appConfigs[appNameForConfig]) {
      appConfig = appConfigs[appNameForConfig];
    }
    else if (clientId && appConfigs[clientId]) {
      appConfig = appConfigs[clientId];
    }
  }
  return appConfig;
}

export async function initAppSettings() {
  // Redirect password reset URLs to standard URL with appropriate clientId based on DB instance
  redirectPasswordResetByDbInstance();

  const requestContext = OktaUtil.getRequestContext();
  let preAuthnConfigs: PreAuthnConfigs = {};
  try {
    preAuthnConfigs = await getPreAuthnConfigs();
  }
  finally {
    //Continue setting up app settings even if getting preAuthnConfigs fails
    const originalRedirectUri = getRequestingOriginalRedirectUri();
    const clientId: string = getRequestingClientId(requestContext, originalRedirectUri);
    preAuthnConfigs.appLabel = preAuthnConfigs.appLabel ?? getRawAppName(requestContext);
    const appConfig = getAppConfig(preAuthnConfigs, clientId);
    const returnObject = Object.assign({ ...appConfig }, preAuthnConfigs) as AppSettings;
    returnObject.clientId = clientId;
    returnObject.isAuthnWithLwaClientId = isAuthnWithLwaSpaClient();
    if (!Object.keys(appConfig).length && CLIENT_ID_TO_SUPPORT_ENV_MAPPING[clientId]) {
      // TODO: Remove CLIENT_ID_TO_SUPPORT_ENV_MAPPING condition once we deprecate
      // https://identity.dev.athenahealth.com/signin/forgot-password flow
      // or add clientId app config mapping.
      returnObject.supportEnv = CLIENT_ID_TO_SUPPORT_ENV_MAPPING[clientId];
      returnObject.appType = APP_TYPES.ATHENANET_USER;
    }
    returnObject.originalRedirectUri = originalRedirectUri;
    returnObject.postToOkta = !!(getParsedState().postToOkta);
    returnObject.xRoutingDeployment = getPreAuthnRequestPayload()?.xRoutingDeployment;
    // Parse temp_pass_hint from URL parameter
    if (originalRedirectUri) {
      const tempPassHintParam = originalRedirectUri.searchParams.get('temp_pass_hint');
      returnObject.tempPasswordHint = tempPassHintParam === 'patient';
    }
    returnObject.unset = (key: keyof PreAuthnConfigs) => {
      if (key in preAuthnConfigs) {
        delete preAuthnConfigs[key];
        delete returnObject[key];
      }
    };
    returnObject.lwaUniqId = setAndGetLwaUniqId();
    returnObject.isDegradedState = isDegradedState();
    global.settings = returnObject;
    if ((!appConfig || Object.keys(appConfig).length === 0) && isRequestFromOidcClient()) {
      AmplitudeUtils.logCustomEvent('AppConfigMissing', { clientId, appLabel: settings.appLabel });
    }
  }

}

function getAppNameForConfig(rawAppName?: string) {
  if (rawAppName && (rawAppName.includes('-') || rawAppName.includes(' '))) {
    return rawAppName.replace(/-/g, ' ').replace(/ +/g, '-')
      .toLowerCase();
  }
  return rawAppName;
}

function getOriginalAuthorizeParams() {
  const originalRedirectUri = getRequestingOriginalRedirectUri();
  const parsedState = getParsedState();
  if (originalRedirectUri) {
    const searchParams = originalRedirectUri?.searchParams;
    const clientId = searchParams?.get('client_id');
    const scope = searchParams?.get('scope');
    const state = searchParams?.get('state');
    const aud = searchParams?.get('aud');
    const redirectUri = searchParams?.get('redirect_uri');
    return { clientId, scope, state, aud, uiLocales: searchParams?.get('ui_locales'), redirectUri };
  }
  else if (parsedState.postToOkta) {
    const originalAuthorizeParams = parsedState.authorizePostParamsObject as Record<string, string>;
    const clientId = originalAuthorizeParams.client_id as string;
    const scope = originalAuthorizeParams.scope;
    const state = originalAuthorizeParams.state;
    const aud = originalAuthorizeParams.aud;
    const redirectUri = originalAuthorizeParams.redirect_uri;
    return { clientId, scope, state, aud, uiLocales: originalAuthorizeParams.ui_locales, redirectUri };
  }
  else {
    return {};
  }
}

export function getPreAuthnRequestPayload(): PreAuthnRequestPayload {
  try {
    const { preAuthnRequestPayload } = getParsedState();
    return JSON.parse(atob(preAuthnRequestPayload ?? '') || '{}');
  }
  catch (err) {
    logger.error('Failed to parse state', err);
  }
  return {};
}

async function loadPreAuthnConfigs() {
  const originalAuthorizeParams = getOriginalAuthorizeParams();
  const preAuthnRequestPayload = getPreAuthnRequestPayload();

  if (Object.keys(originalAuthorizeParams).length === 0 || Object.keys(preAuthnRequestPayload).length === 0) {
    return {};
  }
  let lwaClientInteractionCodeFlowProps;
  if (preAuthnRequestPayload.emailToken) {
    const {
      authentication: {
        request: { redirect_uri: redirectUriSpa, scope: lwaSpaScopes },
      },
    } = OktaUtil.getRequestContext();

    const lwaSpaClientId = getClientId(OktaUtil.getRequestContext()) as string;
    lwaClientInteractionCodeFlowProps = {
      clientId: lwaSpaClientId,
      redirectUri: redirectUriSpa as string,
      scope: lwaSpaScopes,
      codeChallenge: preAuthnRequestPayload.oauthCodeChallenge as string,
      state: nanoid(),
    };
  }

  return await fetchPreAuthnConfigs({
    ...originalAuthorizeParams,
    emailToken: preAuthnRequestPayload.emailToken,
    fhir: preAuthnRequestPayload.fhir,
    authServerId,
    idp: preAuthnRequestPayload.idp,
    lwaUniqId: setAndGetLwaUniqId(),
    lwaClientInteractionCodeFlowProps,
    xRoutingDeployment: preAuthnRequestPayload?.xRoutingDeployment,
  });
}

async function getPreAuthnConfigs() {
  return (isAuthnWithLwaSpaClient() || isAuthnWithDirectOhswPreAuthnClient()) ? await loadPreAuthnConfigs() : {};
}

function isAuthnWithLwaSpaClient(): boolean {
  const authnClientId = getClientId(OktaUtil.getRequestContext());
  return Object.keys(getPreAuthnRequestPayload()).length > 0 && isLwaSpaClient(authnClientId);
}

// Some OHSW apps do not go through LWA, so we call pre-authn directly
// to parse the emailToken and retrieve the stateHandle for the registration flow.
function isAuthnWithDirectOhswPreAuthnClient(): boolean {
  const requestContext = OktaUtil.getRequestContext();
  const authnClientId = getClientId(requestContext);
  const appConfig = getAppConfig({ appLabel: getRawAppName(requestContext) }, authnClientId);
  return Object.keys(getPreAuthnRequestPayload()).length > 0 &&
    !!appConfig.appType &&
    DIRECT_OHSW_PRE_AUTHN_APP_TYPES.includes(appConfig.appType);
}

function isLwaSpaClient(appId?: string): boolean {
  return !!appId && lwaSpaClientIds.includes(appId);
}

/**
 * Checks if the current session is in degraded state
 * by reading the is_degraded_state query parameter.
 * This flag is set by SPA when it degrades to OHSW.
 */
function isDegradedState(): boolean {
  try {
    const currentUrl = new URL(window.location.href);
    const isDegradedStateValue = currentUrl.searchParams.get('is_degraded_state');
    return isDegradedStateValue === 'true';
  }
  catch {
    return false;
  }
}

export const exposedForTesting = {
  getAppNameForConfig,
  getOriginalAuthorizeParams,
  getParsedState,
  getPreAuthnRequestPayload,
  loadPreAuthnConfigs,
  getAppConfig,
  getRequestingClientId,
};
